import { ReactComponent as IconDataExchange } from '@/assets/svgs/data-element-icon.svg';
import { ReactComponent as IconInfoDynamicFill } from '@/assets/svgs/information-circle-dynamic-fill.svg';
import { ReactComponent as IconTick } from '@/assets/svgs/tick.svg';
import Tooltip from "@/componentsDS/Tooltip/index";
import Button from '@/componentsDS/Button/Button'
import { ButtonSize, ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import Dropdown from '@/componentsDS/Dropdown/Dropdown';
import Icon from '@/componentsDS/Icon/Icon';
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Modal from "@/componentsDS/Modal/Modal";
import MultiLayerTable from "@/componentsDS/MultiLayerTable/index";
import Search from '@/componentsDS/Search/index';
import useModal from '@/hooks/use-modal';
import usePage from "@/hooks/use-page/index";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from 'react';
import MultipleEmailAddressInput from '../MultipleEmailAddressInput/index';
import { useSelector } from 'react-redux';
import Toast from '@/components/Toast';
import useToast from '@/hooks/use-toast';
import api from '@/services/api/dataExchangeRelation.js';
import rem from "@/utils/rem";
import StatusTags from "@/pages/onboarding/components/StatusTags/StatusTags";
import MultiLevelDropDown from '@/pages/onboarding/components/MultiLevelMenuDropDown/index';
import { useQuery } from "@/lib/router-dom";
import titleCase from '@/utils/title-case';
import { categorizeFilters, extractUniqueValuesUsingMap, filterDropdownHandler, generateQueryString, searchRecords, updateFilterByParams } from '@/pages/onboarding/helpers/index'
import { useSearchParams } from 'react-router-dom';
import InputDropdownDS from '@/componentsDS/InputDropdown/index'
import { orderBy } from 'lodash';
import { featureConstant } from "@/constants/configuration/feature";
import { useFeature } from "@/hooks/use-feature/index.js";
import classname from 'classnames';

export const RenderGroupByTrigger = ({ groupByDropdownOptions }) => {
	return (
		<span>
			<Icon name="list-view" style="solid" size="medium" /> <span>Group By: {groupByDropdownOptions[0]?.label}</span>
		</span>
	)
}

export const RenderPitstopName = ({ name }) => {
	return <span className='data-exchange-table--subText '>{`${name}`}</span>
}

export function getModalHeading(isEdit) {
	return isEdit ? "Edit Relationship" : "Add Relationship"
}

export function isButtonDisabled({
	selectOrganizationsItems,
	selectServiceProviders,
	selectPitstops,
	emailListServiceProviders,
	emailListOrganizations,
	selectDataElementContribute,
	selectDataElementConsume,
	orgEmail,
	spEmail,
	isOrgEmailValid,
	isSpEmailValid,
	sendCopyToPitstop,
	selectedPitstop,
}) {
	const hasOrganizations = selectOrganizationsItems?.length > 0;
	const hasServiceProviders = selectServiceProviders?.length > 0;

	// Providing data => pitstops must be selected
	const isProvidingData = selectServiceProviders?.some(item => item.value === 'providingData');
	const pitstopConditionMet = !isProvidingData || (selectPitstops?.length > 0);

	// Invite new user - service provider email logic
	const isInvitingSP = selectServiceProviders?.some(item => item.value === 'inviteNewUser');
	const isSpEmailValidForInvite =
		(emailListServiceProviders.length === 0 && spEmail.length > 0 && isSpEmailValid) ||
		(emailListServiceProviders.length > 0 && spEmail.length > 0 && isSpEmailValid) ||
		(emailListServiceProviders.length > 0 && spEmail.length === 0);

	const spInviteConditionMet = !isInvitingSP || isSpEmailValidForInvite;

	// Invite new user - organization email logic
	const isInvitingOrg = selectOrganizationsItems?.some(item => item.value === 'inviteNewUser');
	const isOrgEmailValidForInvite =
		(emailListOrganizations.length === 0 && orgEmail.length > 0 && isOrgEmailValid) ||
		(emailListOrganizations.length > 0 && orgEmail.length > 0 && isOrgEmailValid) ||
		(emailListOrganizations.length > 0 && orgEmail.length === 0);

	const orgInviteConditionMet = !isInvitingOrg || isOrgEmailValidForInvite;

	// Data elements
	const hasDataElements = (selectDataElementContribute?.length > 0) || (selectDataElementConsume?.length > 0);

	const isAllValid =
		hasOrganizations &&
		hasServiceProviders &&
		pitstopConditionMet &&
		spInviteConditionMet &&
		orgInviteConditionMet &&
		hasDataElements;

	// Disable the button if NOT all valid
	return !isAllValid || sendCopyToPitstop && !selectedPitstop?.length;
}

export function getStatus(viaStatus, toStatus) {
	switch (viaStatus) {
		case "NA":
			return getNAStatus(toStatus);
		case "APPROVED":
			return getApprovedStatus(toStatus);
		case "PENDING":
			return getPendingStatus(toStatus);
		case "REJECTED":
			return getRejectedStatus(toStatus);
		case "REVOKED":
			return getRejectedStatus(toStatus);
		default:
			return null;
	}
}

export function getNAStatus(toStatus) {
	switch (toStatus) {
		case "APPROVED":
			return 1;
		case "PENDING":
			return 5;
		case "REJECTED":
			return 7;
		case "REVOKED":
			return 4;
		case "PENDING_YOUR_APPROVAL":
			return 6;
		default:
			return null;
	}
}

export function getApprovedStatus(toStatus) {
	switch (toStatus) {
		case "APPROVED":
			return 2;
		case "PENDING":
			return 5;
		case "REJECTED":
			return 7;
		case "REVOKED":
			return 4;
		default:
			return null;
	}
}

export function getRejectedStatus(toStatus) {
	switch (toStatus) {
		case "APPROVED":
			return 3;
		case "REVOKED":
			return 4;
		case "PENDING":
			return 5;
		case "REJECTED":
			return 7;
		default:
			return null;
	}
}

export function getPendingStatus(toStatus) {
	switch (toStatus) {
		case "APPROVED": case "PENDING": case "REJECTED": case "PENDING_YOUR_APPROVAL":
			return 5;
		case "REVOKED":
			return 4;
		default:
			return null;
	}
}

export const returnServiceProvider = (item, orgId) => {
	if (item === 'inviteNewUser') {
		return null
	}
	return item === 'providingData' ? orgId : item
}

const categoryLabelMap = {
	dataElementName: 'Data Element Name',
	senderOrgName: 'From',
	serviceProviderOrgName: 'Via',
	receiverOrgName: 'To',
	statusLabel: 'Status'
};

const LABEL_ID_MAP = [
	{ labelKey: 'dataElementName', idKey: 'dataElementId' },
	{ labelKey: 'senderOrgName', idKey: 'senderOrgId' },
	{ labelKey: 'serviceProviderOrgName', idKey: 'serviceProviderOrgId' },
	{ labelKey: 'receiverOrgName', idKey: 'receiverOrgId' },
	{ labelKey: 'statusLabel', idKey: 'status' }
]

const TablePage = ({
	title,
	description,
	data,
	emptyMessage,
	refreshData,
	setRefreshData,
	customGroupHeaderRender,
	handleAction,
	organizationItems,
	serviceProvidersItems,
	serviceProviderRelationsLists,
	pitstopItems,
	dataElements,
	dataExchangeLoading,
	orgDataLoading,
	serviceProviderRelationLoading,
	dataElementLoading,
	pitstopsLoading,
}) => {

	const { breadcrumbsItems } = usePage({
		title: title,
		breadcrumbs:[{
			label: '',
			link: `/`,
		},
		{
			label: title,
			link: ``
		}]
	});

	const { dataExchangeData, dataExchangeHeaders } = useRelationshipSetup(data, handleAction, handleEditModal);
	const isCopyTransactionEnabled = useFeature(featureConstant.ENABLE_COPY_TRANSACTION);

	// Get Profile
	const profile = useSelector(state => state.auth.profile);
	const orgId = profile?.organizationId;

	// Dropdown search Handler
	const [dropdownSearch, setDropdownSearch] = useState({
		organizationSearch: '',
		serviceProviderSearch: '',
		dataElementContributeSearch: '',
		dataElementConsumeSearch: '',
		pitstopSearch: ''
	})

	const dropdownSearchHandler = (e) => {
		if (e?.target) {
			const { name, value } = e.target;
			setDropdownSearch(prev => ({ ...prev, [name]: value }));
		}
	}

	const generateLabel = (item) => {
		if (item.serviceProviderRelationshipInvitesId) {
			return "Invite new user"
		}
		else if (item.serviceProviderOrgId === orgId) {
			return "I will provide/consume the data"
		}
		else {
			return item.serviceProviderOrgName
		}
	}

	const generateLabelForOrg = (item) => {
		if (item.initiater === "SENDER") {
			return item.receiverEmail ?? item.receiverOrgName
		}
		else {
			return item.senderEmail ?? item.senderOrgName
		}
	}

	// Dropdown listing
	const [organizationDropdown, setOrganizationDropdown] = useState([]);
	const [serviceProviderDropdown, setServiceProviderDropdown] = useState([]);
	const [pitstopDropdown, setPitstopDropdown] = useState([]);
	const [dataElementContributeDropdown, setDataElementContributeDropdown] = useState([]);
	const [dataElementConsumeDropdown, setDataElementConsumeDropdown] = useState([]);

	// Toast Message
	const toastType = {
		success: "success",
		error: "error"
	}

	const { toastList, addToastMessage } = useToast()
	const [loading, setLoading] = useState(false);

	// Email List
	const [emailListOrganizations, setEmailListOrganizations] = useState([]);
	const [orgEmail, setOrgEmail] = useState("")
	const [isOrgEmailValid, setIsOrgEmailValid]= useState(false)

	const [emailListServiceProviders, setEmailListServiceProviders] = useState([]);
	const [spEmail, setSpEmail]= useState("")
	const [isSpEmailValid, setIsSpEmailValid]= useState(false)

	// const [emailInviteInput, setEmailInviteInput] = useState("")

	// Multi Select Filter Dropdown Items
	const [selectOrganizationsItems, setSelectOrganizationsItems] = useState([]);
	const [selectServiceProviders, setSelectServiceProviders] = useState([]);
	const [selectPitstops, setSelectPitstops] = useState([]);
	const [selectDataElementContribute, setSelectDataElementContribute] = useState([]);
	const [selectDataElementConsume, setSelectDataElementConsume] = useState([]);
	const [selectedPitstop, setSelectedPitstop] = useState([]);
	const [sendCopyToPitstop, setSendCopyToPitstop] = useState(false);
	const [checkIfTransactionFromSender, setCheckIfTransactionFromSender] = useState(true);

	const [isEdit, setIsEdit] = useState(false)
	const [editRow, setEditRow] = useState({})

	const [groupByDropdownOptions, setGroupByDropdownOptions] = useState([{ id: 'dataElementName', label: 'Data Element' }]);

	// Query Params
	const [, setSearchParam] = useSearchParams()
	const query = useQuery();
	const filterKeys = ['dataElement', 'status', 'from', 'via', 'to']
	const [dataElementQueryParams, statusQueryParams, fromQueryParams, viaQueryParams, toQueryParams] = filterKeys.map((key) => query.get(key))

	const [filterDropdownOptions, setFilterDropdownOptions] = useState([]);

	useEffect(() => {
		const inviteEmail = { id: 'inviteNewUser', label: 'Invite New User', value: 'inviteNewUser' };
		const providingData = { id: 'providingData', label: 'I will provide/consume the data', value: 'providingData' };
		// For Organization Dropdown
		if (organizationItems?.length > 0) {
			const orgDropdown = organizationItems.filter((org) => org.id != orgId).map((org) => { return { value: org.id, label: org.name, id: org.id } });
			setOrganizationDropdown([inviteEmail, ...orgDropdown]);
		}
		// For Service Provider Dropdown
		if (serviceProvidersItems?.length > 0) {
			let serviceProviderDropdown = [];
			const consentedSPLists = serviceProviderRelationsLists?.filter((option) => option.serviceProviderRelationshipStatus === "APPROVED");
			if (!isEdit) {
				serviceProviderDropdown = orderBy(
					serviceProvidersItems.filter((org) => org.id != orgId).map((org) => {
						return { value: org.id, label: org.name, id: org.id, category: consentedSPLists?.some(spOrgId => spOrgId.serviceProviderOrgId === org.id) ? "Consented Solution Provider" : "Other Participants" }
					}), ['category'], ['asc']
				);
			}
			else {
				const isContributing = editRow.initiater === 'SENDER';
				const spFlow = isContributing ? 'SEND' : 'RECEIVE';
				serviceProviderDropdown = orderBy(
					serviceProvidersItems.filter((org) => org.id != orgId).map((org) => {
						return { value: org.id, label: org.name, id: org.id, category: consentedSPLists.some(spOrgId => (spOrgId.serviceProviderOrgId === org.id && editRow.dataElementId === spOrgId.dataElementId && spFlow === spOrgId.flow)) ? "Consented Solution Provider" : "Other Participants" }
					}), ['category'], ['asc']
				);
			}
			setServiceProviderDropdown([inviteEmail, providingData, ...serviceProviderDropdown]);
		}
		// For Pitstop Dropdown
		if (pitstopItems?.length > 0) {
			const pitstopDropdown = pitstopItems.map((pitstop) => { return { value: pitstop.systemId, label: pitstop.systemName, id: pitstop.systemId, orgId: pitstop.orgId } });
			setPitstopDropdown(pitstopDropdown);
		}
		// For Data Element Contribute/Consume Dropdown
		if (dataElements?.length > 0) {
			const dataElementDropdown = dataElements.map((dataelement) => { return { value: dataelement.dataElementId, label: dataelement.elementName, id: dataelement.dataElementId } });
			setDataElementContributeDropdown(dataElementDropdown);
			setDataElementConsumeDropdown(dataElementDropdown);
		}
	}, [organizationItems, serviceProvidersItems, pitstopItems, dataElements, isEdit, editRow]);

	const pitstopDropdownHandler =(selectedItem) =>{
		setSelectPitstops(() => [ ...selectedItem])
	}

	// dropdown filter handler function
	const updateFilterDropdown = (param) => {
		setFilterDropdownOptions(param)
		const filterSchema = {
			dataElement: ['Data Element Name'],
			status: ['Status'],
			from: ["From"],
			via: ["Via"],
			to: ["To"]
		};
		const filters = categorizeFilters(param, filterSchema)
		const query = generateQueryString(filters)
		setSearchParam(query)
	}

	useEffect(() => {
		if (!data) return;
		let updatedFilters = [];

		if (dataElementQueryParams) {
			const result = updateFilterByParams(data, dataElementQueryParams, 'dataElementId', 'dataElementName', 'Data Element Name', 'dataExchange')
			if (result.length > 0) {
				updatedFilters = [...result]
			}
		}

		if (viaQueryParams) {
			let viaFilter = []
			const result = updateFilterByParams(data, viaQueryParams, 'serviceProviderOrgId', 'serviceProviderOrgName', 'Via', 'dataExchange')
			if (viaQueryParams.split(',').includes('No Solution Provider')) {
				result.push({
					id: 'no_service_provider',
					label: 'No Solution Provider',
					category: "Via"
				})
			}
			if (result.length > 0) {
				updatedFilters = [...updatedFilters, ...result, ...viaFilter]
			}
		}

		if (toQueryParams) {
			const result = updateFilterByParams(data, toQueryParams, 'receiverOrgId', 'receiverOrgName', 'To', 'dataExchange')
			if (result.length > 0) {
				updatedFilters = [...updatedFilters, ...result]
			}
		}

		if (statusQueryParams) {
			const statusIdMapper = {
				APPROVED: 2,
				ACTIVE: 2,
				INACTIVE: 3,
				REVOKED: 4,
				PENDING: 5,
				'PENDING_YOUR_APPROVAL': 6,
				REJECTED: 7
			}

			statusQueryParams.split(',').forEach(status => {
				updatedFilters.push({
					id: parseInt(statusIdMapper[status.toUpperCase()]),
					label: status.toUpperCase(),
					category: "Status"
				});
			})
		}

		if (fromQueryParams) {
			const result = updateFilterByParams(data, fromQueryParams, 'senderOrgId', 'senderOrgName', 'From', 'dataExchange')
			if (result.length > 0) {
				updatedFilters = [...updatedFilters, ...result]
			}
		}

		if (updatedFilters.length > 0) {
			setFilterDropdownOptions(prevFilters => [...prevFilters, ...updatedFilters]);
		}

		return () => {
			setFilterDropdownOptions([])
		}
	}, [dataElementQueryParams, statusQueryParams, fromQueryParams, viaQueryParams, toQueryParams, data]);

	useEffect(() => {
		if (selectServiceProviders?.find(item => item.value === 'providingData') && pitstopItems?.length === 1) {
			setSelectPitstops(pitstopItems.map((pitstop) => { return { value: pitstop.systemId, label: pitstop.systemName, id: pitstop.systemId, orgId: pitstop.orgId } }));
		}
		else {
			setSelectPitstops([]);
		}
	}, [selectServiceProviders]);

	/*=================================================
		Modal
	/================================================*/
	const { visible: initiateAddRelationshipModal, show: showInitiateAddRelationshipModal, hide: hideInitiateAddRelationshipModal } = useModal()

	function reset() {
		setSelectOrganizationsItems([]);
		setSelectServiceProviders([]);
		setSelectPitstops([]);
		setSelectDataElementContribute([]);
		setSelectDataElementConsume([]);
		setSelectedPitstop([]);
		setSendCopyToPitstop(false);
		setEmailListOrganizations([]);
		setEmailListServiceProviders([]);
		setDropdownSearch({
			organizationSearch: '',
			serviceProviderSearch: '',
			dataElementContributeSearch: '',
			dataElementConsumeSearch: '',
			pitstopSearch: ''
		});
	}

	function openModal() {
		reset();
		showInitiateAddRelationshipModal();
	}

	function toggleSendCopyToPitstop() {
		setSendCopyToPitstop(!sendCopyToPitstop);
		if (pitstopDropdown.length === 1) {
			setSelectedPitstop([pitstopDropdown[0]]);
		} else {
			setSelectedPitstop([]);
		}
	}

	const handleSPDropdown = (selectedItems) => {
		setSelectServiceProviders(selectedItems);
		if (isEdit && selectedItems.length === 1) {
			if (selectedItems[0].id === 'inviteNewUser') {
				dropdownSearchHandler({ target: { name: 'serviceProviderSearch', value: 'Invite New User' } })
			}
			else if (selectedItems[0].id === 'providingData') {
				setSendCopyToPitstop(false);
				setSelectedPitstop([]);
				dropdownSearchHandler({ target: { name: 'serviceProviderSearch', value: 'I will provide/consume the data' } })
			}
			else {
				dropdownSearchHandler({ target: { name: 'serviceProviderSearch', value: selectedItems[0].label } })
			}
		}
		else {
			if (isCopyTransactionEnabled && selectedItems?.length > 0 && selectedItems?.find(item => item.value !== 'providingData')) {
				setSendCopyToPitstop(true);
				if (pitstopDropdown.length === 1) {
					setSelectedPitstop([pitstopDropdown[0]]);
				} else {
					setSelectedPitstop([]);
				}
			}
			else {
				setSendCopyToPitstop(false);
				setSelectedPitstop([]);
			}
			dropdownSearchHandler({ target: { name: 'serviceProviderSearch', value: "" } })
		}
	}

	function setter(selectedRow) {
		setDropdownSearch({
			organizationSearch: generateLabelForOrg(selectedRow),
			serviceProviderSearch: generateLabel(selectedRow),
			dataElementContributeSearch: selectedRow.initiater === "SENDER" ? selectedRow.dataElementName : '',
			dataElementConsumeSearch: selectedRow.initiater === "RECEIVER" ? selectedRow.dataElementName : '',
			pitstopSearch: ''
		});
		const { dataElementId, initiater, senderOrgId, receiverOrgId, serviceProviderOrgId, serviceProviderEmail } = selectedRow;
		setCheckIfTransactionFromSender(senderOrgId === orgId);
		receiverOrgId === orgId ? setSelectDataElementConsume(dataElementId) : setSelectDataElementContribute(dataElementId)
		initiater === 'SENDER' ? setSelectOrganizationsItems([{ value: receiverOrgId }]) : setSelectOrganizationsItems([{ value: senderOrgId }])
		serviceProviderOrgId === orgId ? setSelectServiceProviders([{ value: 'providingData', label: 'I will provide/consume the data', id: 'providingData' }]) :
			serviceProviderOrgId ? setSelectServiceProviders([{ value: serviceProviderOrgId }]) : setSelectServiceProviders([{ value: 'inviteNewUser', label: 'Invite New User', id: 'inviteNewUser' }])
		serviceProviderEmail && setEmailListServiceProviders([serviceProviderEmail])
		setSelectPitstops([])
		if (selectedRow.ccToContributor) {
			setSendCopyToPitstop(true);
			setSelectedPitstop([pitstopDropdown.find(pitstop => pitstop.value === selectedRow.contributorSystemId)])
		}
	}

	function handleEditModal(data) {
		openModal();
		setEditRow(data);
		setIsEdit(true);
		setter(data);
	}

	const addCcIfRequired = () => ({
		ccToContributor: !!sendCopyToPitstop,
		...(sendCopyToPitstop && {
			contributorOrgId: selectedPitstop[0]?.orgId,
			contributorSystemId: selectedPitstop[0]?.id
		}),
	});

	async function handleSubmit() {
		setLoading(true);
		const dataExchangeLists = [];
		const serviceProvidersLists = [];

		const createBody = (initiater, flow, dataElement, orgItems, isServiceProvider) => {
			return isServiceProvider
				? {
					"dataOwnerOrgId": orgId,
					"serviceProviderOrgId": orgItems,
					"dataElementId": dataElement,
					"initiater": "DATA_OWNER",
					"flow": flow
				}
				: {
					"senderOrgId": initiater === "SENDER" ? orgId : orgItems,
					"receiverOrgId": initiater === "RECEIVER" ? orgId : orgItems,
					"dataElementId": dataElement,
					"initiater": initiater
				};
		};

		const createEmailBody = (initiater, flow, dataElement, email, isServiceProvider) => {
			let emailBody;
			if (isServiceProvider) {
				emailBody = {
					"dataOwnerOrgId": orgId,
					"invitedViaEmail": email,
					"dataElementId": dataElement,
					"initiater": "DATA_OWNER",
					"flow": flow
				};
			} else {
				emailBody = initiater === "SENDER" ? {
					"senderOrgId": orgId,
					"invitedEmail": email,
					"dataElementId": dataElement,
					"initiater": initiater
				} : {
					"invitedEmail": email,
					"receiverOrgId": orgId,
					"dataElementId": dataElement,
					"initiater": initiater
				};
			}
			return emailBody;
		};

		const createPitstopsBody = (flow, dataElement) => {
			return {
				"dataOwnerOrgId": orgId,
				"serviceProviderOrgId": orgId,
				"dataElementId": dataElement,
				"initiater": "DATA_OWNER",
				"flow": flow
			};
		};

		const processDataElements = (dataElements, initiater, flow) => {
			const addDataExchange = (dataElement, orgItems) => {
				if (orgItems === 'inviteNewUser') {
					if(orgEmail) {
						dataExchangeLists.push({
							...createEmailBody(initiater, flow, dataElement, orgEmail, false),
							...(isCopyTransactionEnabled ? addCcIfRequired() : {}),
						});
					}
					emailListOrganizations.forEach(email => {
						dataExchangeLists.push({
							...createEmailBody(initiater, flow, dataElement, email, false),
							...(isCopyTransactionEnabled ? addCcIfRequired() : {}),
						});
					});
				}
				else {
					dataExchangeLists.push({
						...createBody(initiater, flow, dataElement, orgItems, false),
						...(isCopyTransactionEnabled ? addCcIfRequired() : {}),
					});
				}
			};

			const addServiceProvider = (dataElement, orgItems) => {
				if (orgItems === 'providingData') {
					serviceProvidersLists.push(createPitstopsBody(flow, dataElement));
				}
				else if (orgItems === 'inviteNewUser') {
					if(spEmail) {
						serviceProvidersLists.push(createEmailBody(initiater, flow, dataElement, spEmail, true));
					}
					emailListServiceProviders.forEach(email => {
						serviceProvidersLists.push(createEmailBody(initiater, flow, dataElement, email, true));
					});
				}
				else {
					serviceProvidersLists.push(createBody(initiater, flow, dataElement, orgItems, true));
				}
			};

			dataElements?.forEach((dataElement) => {
				selectOrganizationsItems.forEach(orgItems => { addDataExchange(dataElement.value, orgItems.id) });
				selectServiceProviders.forEach(orgItems => { addServiceProvider(dataElement.value, orgItems.id) });
			});
		};

		if (selectDataElementContribute.length) {
			processDataElements(selectDataElementContribute, "SENDER", "SEND");
		}

		if (selectDataElementConsume.length) {
			processDataElements(selectDataElementConsume, "RECEIVER", "RECEIVE");
		}

		const body = {
			"dataExchange": dataExchangeLists,
			"serviceProviders": serviceProvidersLists,
			"pitstops":pitstopDropdown.length===1 ?[ pitstopDropdown[0].id ]: selectPitstops?.map((value) => value.id)
		}

		const [error] = await api.addDataExchangeRelationship(body);
		setLoading(false)
		if (error) {
			return addToastMessage(error.message ?? "Failed to add Data Exchange Relationship.", toastType.error)
		}

		hideInitiateAddRelationshipModal();
		addToastMessage("Data exchange relationship requests sent!", toastType.success)
		return setRefreshData(!refreshData);
	}

	async function handleEditSubmit(rowData) {
		setLoading(true);
		const { serviceProviderRelationshipId, serviceProviderRelationshipInvitesId, dataExchangeRelationshipId, dataExchangeRelationshipInvitesId } = rowData
		const payload = {
			"serviceProviderRelationshipId": serviceProviderRelationshipId,
			"serviceProviderRelationshipInvitesId": serviceProviderRelationshipInvitesId,
			"dataExchangeRelationshipId": dataExchangeRelationshipId,
			"dataExchangeRelationshipInvitesId": dataExchangeRelationshipInvitesId,
			"pitstops": selectPitstops?.map((value) => value.id),
			serviceProviderOrgId: returnServiceProvider(selectServiceProviders[0].value, orgId),
			...addCcIfRequired()
		}		
		if (selectServiceProviders?.find(item => item.value === 'inviteNewUser')) {
			payload.invitedViaEmail = emailListServiceProviders[0];
		}
		const [error] = await api.editDataExchangeRelationship(payload);
		setLoading(false);
		if(error) {
			return addToastMessage(error.message ?? "Failed to update Data Exchange Relationship.", toastType.error)
		}

		reset()
		setIsEdit()
		hideInitiateAddRelationshipModal();
		addToastMessage("Data exchange relationship updated", toastType.success)
		return setRefreshData(!refreshData);
	}

	/*=================================================
		Search
	/================================================*/
	const [searchValueForDataExchange, setSearchValueForDataExchange] = useState('');

	/*=================================================
		Filter
	/================================================*/
	const finalFilterValues = LABEL_ID_MAP.flatMap(item => extractUniqueValuesUsingMap(dataExchangeData, item.labelKey, item.idKey, orgId, categoryLabelMap, 'dataExchange'))

	/*=================================================
		Search and Filter Logic
	/================================================*/
	const filteredData = useMemo(() => {
		let filtered = dataExchangeData;

		if (filterDropdownOptions.length > 0) {
			filtered = filterDropdownHandler(dataExchangeData, filterDropdownOptions)
		}

		if (searchValueForDataExchange) {
			const fieldsToSearch = ['dataElementName', 'senderOrgName', 'serviceProviderOrgName', 'receiverOrgName'];
			filtered = filtered.filter(item => searchRecords(item, fieldsToSearch, searchValueForDataExchange));
		}

		if (groupByDropdownOptions.length > 0) {
			const label = groupByDropdownOptions[0].label;
			const updatedOrgName = 'Myself';

			filtered = filtered?.map(item => {
				if (label === 'From' && orgId === item.senderOrgId) {
					return { ...item, senderOrgName: updatedOrgName };
				} else if (label === 'Via' && orgId === item.serviceProviderOrgId) {
					return { ...item, serviceProviderOrgName: updatedOrgName };
				} else if (label === 'To' && orgId === item.receiverOrgId) {
					return { ...item, receiverOrgName: updatedOrgName };
				}
				return item;
			});
		}

		return filtered;
	}, [dataExchangeData, filterDropdownOptions, searchValueForDataExchange, groupByDropdownOptions]);

	/*=================================================
		Group By
	/================================================*/
	const groupByOptions = [
		{ id: 'dataElementName', label: 'Data Element' },
		{ id: 'senderOrgName', label: 'From' },
		{ id: 'serviceProviderOrgName', label: 'Via' },
		{ id: 'receiverOrgName', label: 'To' },
		{ id: 'statusLabel', label: 'Status' }
	];

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{title}</div>
				</div>
			</div>

			<div className="title-header--sub-header">{description}</div>

			<div className="title-header__actionable">
				<div className="title-header__actionable__left">
					<Search value={searchValueForDataExchange || ''} onChange={(e) => setSearchValueForDataExchange(e?.target?.value ?? e)} onCancel={() => setSearchValueForDataExchange('')} id="my-data-exchange-search" />

					<Dropdown
						options={finalFilterValues}
						value={filterDropdownOptions}
						onAdd={updateFilterDropdown}
						multiple={true}
						allowSelectAll={true}
						trigger={
							<span>
								<Icon name="adjustments-horizontal" style="solid" size="medium" /> <span>Filter</span>
							</span>
						}
						size="default"
						setWidth={500}
						isLoading={dataExchangeLoading}
						noResults={data.length === 0}
						id="my-data-exchange-filter-dropdown-items"
					/>

					<Dropdown
						options={groupByOptions}
						value={groupByDropdownOptions}
						onAdd={setGroupByDropdownOptions}
						trigger={<RenderGroupByTrigger groupByDropdownOptions={groupByDropdownOptions} />}
						size="default"
						setWidth={228}
						id="my-data-exchange-group-by-dropdown-items"
					/>
				</div>

				<div className="title-header__actionable__right">
					<div className="data-exchange-actions-header">
						<div className="data-exchange-actions-header__left">
						</div>
						<div className="data-exchange-actions-header__right">
							<Button
								variant={ButtonVariant.STANDARD}
								type={ButtonType.SOLID}
								data-testid="add-relationship-button"
								text={"+ Add Relationship"}
								onClick={() => {
									openModal();
								}}
								id="my-data-exchange-add-relationship-button"
							/>
						</div>
					</div>
				</div>
			</div>

			<MultiLayerTable
				data={filteredData}
				headers={dataExchangeHeaders}
				groupBy={groupByDropdownOptions[0]?.id}
				rowSize="large"
				emptyMessage={emptyMessage}
				customGroupHeaderRender={customGroupHeaderRender}
				loading={dataExchangeLoading}
				sortBy="modified"
				id="my-data-exchange-column-sort-"
			/>

			<Modal visible={initiateAddRelationshipModal} close={() => {
				hideInitiateAddRelationshipModal()
				setIsEdit(false)
				reset()
			}} dismissClickOutsideModal={false}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className='card-configure__title__left__icon'>
											<IconDataExchange />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											<div className='card-configure__title__right__top__title'>
												{getModalHeading(isEdit)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="card-configure__body__dropdown">
								<div className="card-configure__body__dropdown__title card-configure__body__dropdown__title--info">
								<IconInfoDynamicFill className='card-configure__body__dropdown__with-icon-image' />
								Fields marked with (<span className='text--asterisk'>*</span>) are required.
								</div>
								<span className="card-configure__body__dropdown__title">
								<span className='text--asterisk'>*</span>	With
								</span>
								<InputDropdownDS
									disabled={isEdit}
									value={dropdownSearch.organizationSearch}
									onChange={dropdownSearchHandler}
									name={'organizationSearch'}
									dropdownOption={organizationDropdown}
									placeholder={"Select Organization(s)"}
									onSelectDropdownItem={setSelectOrganizationsItems}
									multipleSelection={!isEdit}
									dropdownSelectedOption={selectOrganizationsItems}
									fullWidth
									isLoading={orgDataLoading}
								/>
							</div>
							{/* If user has selected 'To invite new user' in To dropdown */}
							{selectOrganizationsItems?.find(item => item.value === 'inviteNewUser') &&
								<MultipleEmailAddressInput
									emailList={emailListOrganizations}
									setEmailList={setEmailListOrganizations}
									email ={orgEmail}
									setEmail={setOrgEmail}
									emailText={"Can't find the Organization you are looking for?"}
									validateExistingEmail={true}
									isTypedEmailValid={isOrgEmailValid}
									setIsTypedEmailValid={setIsOrgEmailValid}

								/>
							}

							<div className="card-configure__body__dropdown">
								<span className="card-configure__body__dropdown__title card-configure__body__dropdown__with-icon">
									<div className='card-configure__body__dropdown__with-icon-text'><span className='text--asterisk'>*</span> Via</div>
									<Tooltip id="relationship-popup-via-tooltip" placement="bottom-start" content="Select the Solution Provider that will provide or consume data on your behalf.">
										<IconInfoDynamicFill className='card-configure__body__dropdown__with-icon-image' />
									</Tooltip>
								</span>
								<div className="card-configure__body__dropdown__option">
									<InputDropdownDS
										placeholder={"Select Solution Provider(s)"}
										value={dropdownSearch.serviceProviderSearch}
										onChange={dropdownSearchHandler}
										dropdownOption={serviceProviderDropdown}
										onSelectDropdownItem={handleSPDropdown}
										multipleSelection={!isEdit}
										dropdownSelectedOption={selectServiceProviders}
										name={'serviceProviderSearch'}
										fullWidth
										isLoading={serviceProviderRelationLoading}
									/>
								</div>
							</div>

							{/* If user has selected 'To invite new user' in Via dropdown */}
							{
								selectServiceProviders?.find(item => item.value === 'inviteNewUser') &&
									<MultipleEmailAddressInput
										emailList={emailListServiceProviders}
										setEmailList={setEmailListServiceProviders}
										email={spEmail}
										setEmail={setSpEmail}
										emailText={"Can't find the Solution Provider you are looking for?"}
										isEdit={isEdit}
										validateExistingEmail={true}
										isTypedEmailValid={isSpEmailValid}
										setIsTypedEmailValid={setIsSpEmailValid}
									/>

							}
							{/* If User has selected 'I will provide/consume the data in Via dropdown and If there are more than 1 Pitstops */}
							{selectServiceProviders?.find(item => item.value === 'providingData') && pitstopDropdown.length > 1 && <div className="card-configure__body__dropdown">
								<span className="card-configure__body__dropdown__title">
									Pitstop
								</span>
								<div className="card-configure__body__dropdown__option">
									<InputDropdownDS
										placeholder={"Select your Pitstop(s) to host"}
										dropdownOption={pitstopDropdown}
										onSelectDropdownItem={pitstopDropdownHandler}
										dropdownSelectedOption={selectPitstops}
										value={dropdownSearch.pitstopSearch}
										onChange={dropdownSearchHandler}
										multipleSelection
										name={'pitstopSearch'}
										fullWidth
										isLoading={pitstopsLoading}
									/>
								</div>
							</div>}

							<hr className='card-configure__separation-line' />
							<div className='card-configure__body__text'>
								<div>Select at least one Data Element to contribute or consume. </div>
								<div>You can leave one field empty.</div>
							</div>

							{/* Dropdown - For sending and receiving the Data Elements */}
							<div className="card-configure__body__dropdown">
								<span className="card-configure__body__dropdown__title">
									Data Element(s) to contribute
								</span>
								<div className="card-configure__body__dropdown__option">
									<InputDropdownDS
										placeholder={"Select Data Element(s)"}
										dropdownOption={dataElementContributeDropdown}
										onSelectDropdownItem={setSelectDataElementContribute}
										dropdownSelectedOption={selectDataElementContribute}
										multipleSelection={!isEdit}
										onChange={dropdownSearchHandler}
										value={dropdownSearch.dataElementContributeSearch}
										name={'dataElementContributeSearch'}
										disabled={isEdit}
										fullWidth
										isLoading={dataElementLoading}
									/>
									{selectDataElementContribute.length === 0 &&
										<div className='card-configure__body__dropdown__title'>
											Not contributing data? You can leave this field empty.
										</div>
									}
								</div>
								{isCopyTransactionEnabled && selectServiceProviders?.length > 0 && selectServiceProviders?.find(item => item.value !== 'providingData') && checkIfTransactionFromSender && <div className="copy-transaction__content__data-elements__col">
									<div className="copy-transactions__content__checkbox">
										<div data-testid="checkbox-element"
											className={classname("copy-checkbox-wrapper", {
												"copy-checkbox-wrapper--active": sendCopyToPitstop,
											})}
											onClick={toggleSendCopyToPitstop}
											role="none"
											>
											{sendCopyToPitstop && <IconTick />}
										</div>
									</div>
									<div className="layout-data-elements__content__text">
										<div className="layout-data-elements__content__top">
											<span className={sendCopyToPitstop ? "copy-transactions__content__checkbox__text--active" : "copy-transactions__content__checkbox__text"}>Send transaction copies to pitstop</span>
										</div>
									</div>
									<Tooltip placement="bottom-start" content="Transaction copies will be sent to the selected pitstop when this Data Element is shared on behalf of your organization.">
										<IconInfoDynamicFill className='card-configure__body__dropdown__with-icon-image' />
									</Tooltip>
								</div>}
							</div>
							{isCopyTransactionEnabled && sendCopyToPitstop && checkIfTransactionFromSender &&<div className="card-configure__body__dropdown">
								<span className="card-configure__body__dropdown__title">
									Pitstop
								</span>
								{pitstopDropdown.length > 1 && <div className="card-configure__body__dropdown__option">
									<Dropdown
										options={pitstopDropdown}
										value={selectedPitstop}
										onAdd={setSelectedPitstop}
										size="default"
										placeholder={"Select your Pitstop to sent these copies"}
										trigger={
											<div className="copy-transaction__single-dropdown__input__body" data-testid="input-prefix-dropdown">
												{selectedPitstop?.[0]?.label ? (
													<span className="copy-transaction__single-dropdown__input__body__text">
														{selectedPitstop[0].label}
													</span>
													) : (
													<span className="copy-transaction__single-dropdown__input__body__placeholder">
														Select your Pitstop to sent these copies
													</span>
												)}
												<div className='DS__input__suffix-icon'>
													<Icon className="DS__icon DS__icon--small" name="chevron-down" style="solid" size="small" />
												</div>
											</div>
										}
										multiple={false}
										offset={[0, 4]}
										fullWidth
									/>
								</div>}
								{pitstopDropdown?.length === 1 && <div className="copy-transaction__single-input__body">
									<div className='copy-transactions__single-input-container'>{selectedPitstop[0]?.label}</div>
								</div>}
							</div>}
							<div className="card-configure__body__dropdown">
								<span className="card-configure__body__dropdown__title">
									Data Element(s) to consume
								</span>
								<div className="card-configure__body__dropdown__option">
									<InputDropdownDS
										placeholder={"Select Data Element(s)"}
										dropdownOption={dataElementConsumeDropdown}
										onSelectDropdownItem={setSelectDataElementConsume}
										dropdownSelectedOption={selectDataElementConsume}
										multipleSelection={!isEdit}
										onChange={dropdownSearchHandler}
										value={dropdownSearch.dataElementConsumeSearch}
										name={'dataElementConsumeSearch'}
										disabled={isEdit}
										fullWidth
										isLoading={dataElementLoading}
									/>
									{
										selectDataElementConsume.length === 0 &&
										<div className='card-configure__body__dropdown__title'>
											Not consuming data? You can leave this field empty.
										</div>
									}
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<Button
								text="Cancel"
								type={ButtonType.GHOST}
								variant={ButtonVariant.STANDARD}
								data-testid="cancel-button"
								onClick={() => {
									reset()
									setIsEdit(false)
									hideInitiateAddRelationshipModal();
								}}
								id="my-data-exchange-edit-cancel-button"
							/>
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							<Button
								text={"Add"}
								size={ButtonSize.DEFAULT}
								type={ButtonType.SOLID}
								variant={ButtonVariant.STANDARD}
								data-testid="add-button"
								disabled={isButtonDisabled({
									selectOrganizationsItems,
									selectServiceProviders,
									selectPitstops,
									emailListServiceProviders,
									emailListOrganizations,
									selectDataElementContribute,
									selectDataElementConsume,
									orgEmail,
									isOrgEmailValid,
									spEmail,
									isSpEmailValid,
									sendCopyToPitstop,
									selectedPitstop,
								})}
								onClick={() => {
									isEdit ? handleEditSubmit(editRow) :
										handleSubmit();
								}}
								id="my-data-exchange-edit-add-button"
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Toast toastList={toastList} />
		</>
	);
};

export const useRelationshipSetup = (data, handleAction, handleEditModal) => {

	// Get Profile
	const profile = useSelector(state => state.auth.profile);
	const orgId = profile?.organizationId;
	const isCopyTransactionEnabled = useFeature(featureConstant.ENABLE_COPY_TRANSACTION);

	const statusMap = {
		1: "APPROVED",
		2: "ACTIVE",
		3: "INACTIVE",
		4: "REVOKED",
		5: "PENDING",
		6: "PENDING_YOUR_APPROVAL",
		7: "REJECTED",
		8: "DISABLED_COPY_TRANSACTION",
		9: "ENABLED_COPY_TRANSACTION"
	};

	const formatedStatusMap = Object.keys(statusMap).reduce((acc, key) => {
		acc[key] = titleCase(statusMap[key]);
		return acc;
	}, {});

	const statusEnum = {
		APPROVED: "APPROVED",
		CANCELLED: "CANCELLED",
		PENDING: "PENDING",
		REVOKED: "REVOKED",
		REJECTED: "REJECTED",
		REMOVE: 'REMOVE',
		PENDING_YOUR_APPROVAL: "PENDING_YOUR_APPROVAL",
		DISABLED_COPY_TRANSACTION: "DISABLED_COPY_TRANSACTION",
		ENABLED_COPY_TRANSACTION: "ENABLED_COPY_TRANSACTION"
	};

	const groupByMyselfKeys = ['senderOrgName', 'serviceProviderOrgName', 'receiverOrgName'];

	function getStatusLabel(statusCode) {
		return statusMap[statusCode] || "UNKNOWN STATUS";
	}

	function getStatusFormatedLabel(statusCode) {
		if (statusCode === 6) return "Pending Your Approval"
		return formatedStatusMap[statusCode] || "UNKNOWN STATUS";
	}

	const dataExchangeData = data?.map((data) => {
		const viaStatus = data.serviceProviderRelationshipStatus ?? "NA";
		let toStatus;
		if (
			data.dataExchangeRelationshipStatus === statusEnum.PENDING &&
			((data.receiverOrgId === orgId && data.initiater === 'SENDER') || (data.senderOrgId === orgId && data.initiater === 'RECEIVER'))
		) {
			toStatus = statusEnum.PENDING_YOUR_APPROVAL
		} else {
			toStatus = data.dataExchangeRelationshipStatus;
		}
		const status = getStatus(viaStatus, toStatus);

		return {
			...data,
			status,
			statusLabel: getStatusFormatedLabel(status),
		};
	});

	const renderVia = (orgName, rowData) => {
		const { serviceProviderOrgId, serviceProviderRelationshipStatus, dataExchangeRelationshipStatus, serviceProviderRelationshipInvitesId, serviceProviderEmail } = rowData;
		if ((serviceProviderOrgId === profile?.organizationId)) {
			return <>
				{'-'}
				<br />
				{serviceProviderRelationshipStatus != dataExchangeRelationshipStatus && serviceProviderOrgId !== profile?.organizationId ? <StatusTags status={serviceProviderRelationshipStatus} /> : ""}
			</>;
		}

		if (serviceProviderRelationshipStatus === dataExchangeRelationshipStatus) {
			return <>{serviceProviderRelationshipInvitesId ? serviceProviderEmail : orgName}</>;
		}

		if (serviceProviderRelationshipStatus === null) {
			return <>{"-"}</>;
		}

		return (
			<>
				{serviceProviderRelationshipInvitesId ? serviceProviderEmail : orgName}
				<br />
				<StatusTags status={serviceProviderRelationshipStatus} />
			</>
		);
	};

	const renderTo = (orgName, rowData) => {
		const { receiverOrgId, senderOrgId, serviceProviderOrgId, serviceProviderRelationshipStatus, dataExchangeRelationshipStatus, dataExchangeRelationshipInvitesId, receiverEmail, pitstopId, initiater } = rowData;
		if ((receiverOrgId === profile?.organizationId)) {
			return <>
				{'Myself'}
				<br />
				{pitstopId && <RenderPitstopName name={pitstopId.systemName} />}
				{
					serviceProviderOrgId && serviceProviderOrgId !== orgId &&
					serviceProviderRelationshipStatus != dataExchangeRelationshipStatus &&
					initiater === "SENDER" &&
					dataExchangeRelationshipStatus !== statusEnum.REVOKED &&
					<StatusTags status={dataExchangeRelationshipStatus} />
				}
			</>;
		}

		return (
			<>
				{dataExchangeRelationshipInvitesId ? receiverEmail : orgName}
				<br />
				{
					serviceProviderRelationshipStatus !== dataExchangeRelationshipStatus &&
					initiater === "SENDER" &&
					dataExchangeRelationshipStatus !== statusEnum.REVOKED &&
					<StatusTags status={dataExchangeRelationshipStatus} />
				}
			</>
		);
	};

	const renderSender = (orgName, rowData) => {
		const { senderOrgId, pitstopId, serviceProviderOrgId, dataExchangeRelationshipStatus, serviceProviderRelationshipStatus, initiater, dataExchangeRelationshipInvitesId, receiverEmail, senderEmail, ccToContributor, contributorPitstopName, receiverOrgId } = rowData;
		const result = `${contributorPitstopName}`;
		const showCopyLabel = isCopyTransactionEnabled && ccToContributor && (senderOrgId === orgId) && dataExchangeRelationshipStatus !== statusEnum.REVOKED;
		if (senderOrgId === profile?.organizationId) {
			return (
				<div className='data-exchange__from-label__container'>
				<div>
					{'Myself'}
					<br />
					{pitstopId && <RenderPitstopName name={pitstopId.systemName} />}
					{
						serviceProviderOrgId && serviceProviderOrgId !== orgId &&
						serviceProviderRelationshipStatus !== dataExchangeRelationshipStatus &&
						initiater === "RECEIVER" &&
						dataExchangeRelationshipStatus !== statusEnum.REVOKED &&
						<StatusTags status={dataExchangeRelationshipStatus} />
					}
					{showCopyLabel && contributorPitstopName && <RenderPitstopName name={result} />}
				</div>
				{showCopyLabel && <div><StatusTags status={"COPY"} /></div>}
				</div>
			)
		}

		return (
			<div className='data-exchange__from-label__container'>
			<div>
				{initiater === "RECEIVER" && (dataExchangeRelationshipInvitesId ? senderEmail : orgName)}
				{initiater === "SENDER" && (dataExchangeRelationshipInvitesId ? receiverEmail : orgName)}
				{
					serviceProviderRelationshipStatus !== dataExchangeRelationshipStatus &&
					initiater === "RECEIVER" &&
					dataExchangeRelationshipStatus !== statusEnum.REVOKED &&
					<StatusTags status={dataExchangeRelationshipStatus} />
				}
				{showCopyLabel && contributorPitstopName && <RenderPitstopName name={result} />}
			</div>
			{showCopyLabel && <div><StatusTags status={"COPY"} /></div>}
			</div>
		);
	}

	const renderAction = (action, rowData) => {
		const menuList = [];
		let hasNotifyDot = false;

		groupByMyselfKeys.forEach(key => {
		  if (rowData[key] === 'Myself') {
			rowData = { ...rowData, [key]: profile.organization.name };
		  }
		});

		const {
		  dataExchangeRelationshipStatus,
		  serviceProviderRelationshipStatus,
		  senderOrgId,
		  receiverOrgId,
		  serviceProviderOrgId,
		  initiater
		} = rowData;

		const isApproved = dataExchangeRelationshipStatus === statusEnum.APPROVED;
		const isPending = dataExchangeRelationshipStatus === statusEnum.PENDING;
		const isRejected = dataExchangeRelationshipStatus === statusEnum.REJECTED;
		const spApproved = serviceProviderRelationshipStatus === statusEnum.APPROVED;
		const spPending = serviceProviderRelationshipStatus === statusEnum.PENDING;
		const spRejected = serviceProviderRelationshipStatus === statusEnum.REJECTED;
		const spRevoked = serviceProviderRelationshipStatus === statusEnum.REVOKED;
		const isInitiatorReceiver = senderOrgId === orgId && initiater === 'RECEIVER';
		const isInitiatorSender = receiverOrgId === orgId && initiater === 'SENDER';
		const isMyOrgCP = isInitiatorReceiver || isInitiatorSender;
		const enabledCopyTransaction = rowData.ccToContributor;
		const disabledCopyTransaction = !enabledCopyTransaction;
		const shouldHideTransactionCopy = senderOrgId === serviceProviderOrgId || senderOrgId !== orgId;

		const editSolutionProviderAction = {
		  label: "Edit Solution Provider",
		  onClick: (e) => {
			e.stopPropagation();
			handleEditModal(rowData);
		  }
		};

		const enableTransactionCopy = {
		  label: "Enable Transaction Copy",
		  onClick: (e) => {
			e.stopPropagation();
			handleAction(rowData, statusEnum.ENABLED_COPY_TRANSACTION);
		  }
		};

		const disableTransactionCopy = {
		  label: "Disable Transaction Copy",
		  onClick: (e) => {
			e.stopPropagation();
			handleAction(rowData, statusEnum.DISABLED_COPY_TRANSACTION);
		  }
		};

		const revokeAction = {
		  label: "Revoke",
		  onClick: (e) => {
			e.stopPropagation();
			handleAction(rowData, statusEnum.REVOKED);
		  }
		};

		const approveAction = {
		  label: "Approve",
		  onClick: (e) => {
			e.stopPropagation();
			handleAction(rowData, statusEnum.APPROVED);
		  }
		};

		const rejectAction = {
		  label: "Reject",
		  onClick: (e) => {
			e.stopPropagation();
			handleAction(rowData, statusEnum.REJECTED);
		  }
		};

		const cancelRequestAction = {
		  label: "Cancel Request",
		  onClick: (e) => {
			e.stopPropagation();
			handleAction(rowData, statusEnum.CANCELLED);
		  }
		};

		// Consolidated conditions for actions
		if (isApproved && (spApproved || spRejected || spRevoked)) {
			menuList.push( editSolutionProviderAction, ...((isCopyTransactionEnabled && disabledCopyTransaction && spApproved && !shouldHideTransactionCopy) ? [enableTransactionCopy] : []), 
			...((isCopyTransactionEnabled && enabledCopyTransaction && spApproved && !shouldHideTransactionCopy) ? [disableTransactionCopy] : []), revokeAction );
		} else if (isPending) {
		  if (serviceProviderRelationshipStatus === null && isMyOrgCP) {
			menuList.push(approveAction, rejectAction);
			hasNotifyDot = true;
		  }
		  if (spPending) {
			menuList.push(cancelRequestAction);
		  }
		  if (spApproved || spRejected || spRevoked) {
			menuList.push(editSolutionProviderAction, cancelRequestAction );
		  }
		} else if ((isApproved || isRejected) && spPending) {
			menuList.push(editSolutionProviderAction, cancelRequestAction);
		}

		return (
		  menuList?.length > 0 &&
		  <div className='on-boarding__action-items'>
			<MultiLevelDropDown
			  menuList={menuList}
			  hasNotifyDot={hasNotifyDot}
			  id="my-data-exchange-action-menu"
			/>
		  </div>
		);
	  };

	const dataExchangeHeaders = [
		{
			key: "dataElementName",
			label: "Data Element",
			width: rem(180),
		},
		{
			key: "senderOrgName",
			label: "From",
			width: rem(180),
			render: (orgName, rowData) => renderSender(orgName, rowData),
		},
		{
			key: "serviceProviderOrgName",
			label: "Via",
			width: rem(180),
			sortTarget: "serviceProviderOrgName",
			render: (orgName, rowData) => renderVia(orgName, rowData),
		},
		{
			key: "receiverOrgName",
			label: "To",
			width: rem(180),
			sortTarget: "receiverOrgName",
			render: (orgName, rowData) => renderTo(orgName, rowData),
		},
		{
			key: "modified",
			label: "Modified",
			width: rem(124),
			render: (modified) => new Date(modified).toLocaleDateString(),
		},
		{
			key: "status",
			label: "Status",
			width: rem(180),
			render: (status) => <StatusTags status={getStatusLabel(status)} />,
		},
		{
			key: "action",
			width: rem(96),
			sortable: false,
			render: (action, rowData) => renderAction(action, rowData)
		},
	];

	return {
		dataExchangeData,
		dataExchangeHeaders,
		getStatusLabel
	}
}

TablePage.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	emptyMessage: PropTypes.string.isRequired,
	refreshData: PropTypes.bool,
	setRefreshData: PropTypes.func,
	customGroupHeaderRender: PropTypes.func,
	handleAction: PropTypes.func,
	organizationItems: PropTypes.arrayOf(PropTypes.object),
	serviceProvidersItems: PropTypes.arrayOf(PropTypes.object),
	serviceProviderRelationsLists: PropTypes.arrayOf(PropTypes.object),
	pitstopItems: PropTypes.arrayOf(PropTypes.object),
	dataElements: PropTypes.arrayOf(PropTypes.object),
	dataExchangeLoading: PropTypes.bool,
	orgDataLoading: PropTypes.bool,
	serviceProviderRelationLoading: PropTypes.bool,
	pitstopsLoading: PropTypes.bool,
	dataElementLoading: PropTypes.bool,
};

RenderGroupByTrigger.propTypes = {
	groupByDropdownOptions: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired
		})
	).isRequired
};

RenderPitstopName.propTypes = {
	name: PropTypes.string.isRequired
};

export default TablePage;
